import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface CalendarDay {
  date: number;
  isCurrentMonth: boolean;
  fullDate: string;
  isSelected: boolean;
}

@Component({
  selector: 'app-custom-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './custom-date-picker.component.html',
  styleUrls: ['./custom-date-picker.component.css']
})
export class CustomDatePickerComponent implements OnInit, OnChanges {
  @Input() value: string = '';
  @Input() align: 'left' | 'right' = 'right';
  @Output() dateSelect = new EventEmitter<string>();
  @Output() closePicker = new EventEmitter<void>();

  currentYear: number = new Date().getFullYear();
  currentMonth: number = new Date().getMonth();

  years: number[] = [];
  months: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  daysOfWeek: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  calendarDays: CalendarDay[] = [];

  ngOnInit(): void {
    this.initYears();
    this.parseInitialValue();
    this.generateCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].firstChange) {
      this.parseInitialValue();
      this.generateCalendar();
    }
  }

  private initYears(): void {
    const currentYr = new Date().getFullYear();
    const startYr = 1920;
    const endYr = currentYr + 15;
    const yearList: number[] = [];
    for (let y = startYr; y <= endYr; y++) {
      yearList.push(y);
    }
    this.years = yearList;
  }

  private parseInitialValue(): void {
    if (!this.value) return;
    const trimmed = this.value.trim();
    if (/^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(trimmed)) {
      const parts = trimmed.split(/[\/-]/);
      const m = parseInt(parts[1], 10) - 1;
      const y = parseInt(parts[2], 10);
      if (!isNaN(y) && !isNaN(m) && m >= 0 && m <= 11) {
        this.currentYear = y;
        this.currentMonth = m;
      }
    } else if (/^\d{4}[\/-]\d{2}[\/-]\d{2}$/.test(trimmed)) {
      const parts = trimmed.split(/[\/-]/);
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      if (!isNaN(y) && !isNaN(m) && m >= 0 && m <= 11) {
        this.currentYear = y;
        this.currentMonth = m;
      }
    }
  }

  generateCalendar(): void {
    const days: CalendarDay[] = [];

    const firstDayIndex = new Date(this.currentYear, this.currentMonth, 1).getDay();
    const totalDaysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
    const prevMonthDays = new Date(this.currentYear, this.currentMonth, 0).getDate();

    // Previous month overflow days
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = prevMonthDays - i;
      const prevMonth = this.currentMonth === 0 ? 11 : this.currentMonth - 1;
      const prevYear = this.currentMonth === 0 ? this.currentYear - 1 : this.currentYear;
      const fullDate = this.formatDateStr(prevYear, prevMonth, dayNum);
      days.push({
        date: dayNum,
        isCurrentMonth: false,
        fullDate,
        isSelected: fullDate === this.value
      });
    }

    // Current month days
    for (let d = 1; d <= totalDaysInMonth; d++) {
      const fullDate = this.formatDateStr(this.currentYear, this.currentMonth, d);
      days.push({
        date: d,
        isCurrentMonth: true,
        fullDate,
        isSelected: fullDate === this.value
      });
    }

    // Next month overflow days (to fill 42 cells)
    const remaining = 42 - days.length;
    for (let j = 1; j <= remaining; j++) {
      const nextMonth = this.currentMonth === 11 ? 0 : this.currentMonth + 1;
      const nextYear = this.currentMonth === 11 ? this.currentYear + 1 : this.currentYear;
      const fullDate = this.formatDateStr(nextYear, nextMonth, j);
      days.push({
        date: j,
        isCurrentMonth: false,
        fullDate,
        isSelected: fullDate === this.value
      });
    }

    this.calendarDays = days;
  }

  private formatDateStr(year: number, monthIndex: number, day: number): string {
    const mStr = String(monthIndex + 1).padStart(2, '0');
    const dStr = String(day).padStart(2, '0');
    return `${dStr}/${mStr}/${year}`;
  }

  prevMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.generateCalendar();
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.generateCalendar();
  }

  onYearChange(newYear: number): void {
    this.currentYear = Number(newYear);
    this.generateCalendar();
  }

  onMonthChange(newMonthIndex: number): void {
    this.currentMonth = Number(newMonthIndex);
    this.generateCalendar();
  }

  selectDay(day: CalendarDay): void {
    this.dateSelect.emit(day.fullDate);
  }

  selectToday(): void {
    const today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth();
    const formatted = this.formatDateStr(this.currentYear, this.currentMonth, today.getDate());
    this.dateSelect.emit(formatted);
  }

  clearDate(): void {
    this.dateSelect.emit('');
  }

  close(): void {
    this.closePicker.emit();
  }
}
